import L from 'leaflet'
import type { FeatureCollection } from 'geojson'
import { BASEMAPS, buildLocationsLayer, buildRiverLayer, buildSpillsLayer, buildTanksLayer, buildTrailLayer, buildWellsLayer, createHelpModal, createLegend } from './MapUtils'

;(globalThis as Record<string, unknown>).L = L

await import('leaflet-hash')
await import('leaflet-easybutton')
await import('leaflet.fullscreen')

const markerIcon2x = new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href
const markerIcon = new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href
const markerShadow = new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow })

export async function initMap(): Promise<L.Map> {
  const riverName = import.meta.env.VITE_RIVER_NAME as string
  const base = import.meta.env.BASE_URL

  const [riverGeoJSON, trailGeoJSON, locationsGeoJSON, spillsGeoJSON, tanksGeoJSON, wellsGeoJSON] = await Promise.all([
    fetch(`${base}/data/${riverName}.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}/data/${riverName}_trail.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}/data/${riverName}_locations.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}/data/${riverName}_spills.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}/data/${riverName}_tanks.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
    fetch(`${base}/data/${riverName}_wells.geojson`).then(r => r.json()) as Promise<FeatureCollection>,
  ])

  const { wells, wells_lp, wells_pa, spills } = riverGeoJSON.features[0].properties as Record<string, any>

  const map = L.map('map', { zoomControl:true, zoomSnap:0.5 })

  const riverLayer = buildRiverLayer(riverGeoJSON).addTo(map)
  const trailLayer = buildTrailLayer(trailGeoJSON).addTo(map)
  const locationsLayer = buildLocationsLayer(locationsGeoJSON).addTo(map)
  const tanksLayer = buildTanksLayer(tanksGeoJSON).addTo(map)
  const spillsLayer = buildSpillsLayer(spillsGeoJSON).addTo(map)
  const wellsLayer = buildWellsLayer(wellsGeoJSON).addTo(map)
  map.setView([40.4488, -104.7768], 13)

  createLegend([
    { label: 'Cache la Poudre River', type: 'line', color: '#1f78b4', weight: 2 },
    { label: 'Poudre River Trail', type: 'line', color: '#734c00', weight: 2, dashArray: '6,6' },
    { label: `Locations 20+ Wells (${locationsGeoJSON.features.length})`, type: 'circle', color: '#12aa4e', fillColor: 'rgba(18,170,78,0.25)', weight: 2},
    { label: `Active Wells (${(wells - wells_pa).toLocaleString()})`, type: 'circle', color: '#c00', fillColor: 'rgba(204,0,0,0.4)', weight: 1 },
    { label: `Low Producing Wells (${(wells_lp ?? 0).toLocaleString()})`, type: 'circle', color: '#f90', fillColor: 'rgba(255,153,0,0.4)', weight: 1 },
    { label: `Plugged Wells (${(wells_pa ?? 0).toLocaleString()})`, type: 'circle', color: '#999', fillColor: 'rgba(153,153,153,0.4)', weight: 1 },
    { label: `Spills (${(spills ?? 0).toLocaleString()})`, type: 'polygon', color: '#ff1493', fillColor: 'rgba(255,105,180,0.2)', weight: 1, rotation: 45 },
    { label: `Tank Batteries (${tanksGeoJSON.features.length.toLocaleString()})`, svg: `${import.meta.env.BASE_URL}/tank.svg` },
  ], 'bottomleft').addTo(map)

    const logoControl = L.Control.extend({
    options: { position: 'bottomleft' },
    onAdd() {
      const container = L.DomUtil.create('div')
      L.DomEvent.disableClickPropagation(container)
      container.innerHTML = `<a href="https://ecocarto.com/" target="_blank"><img src="${base}/logo.png" style="display:block;height:auto;width:180px;"></a>`
      return container
    },
  })
  new logoControl().addTo(map)

  const homeButton = L.easyButton('fa-solid fa-house', (_, map) => map.setView([40.4488, -104.7768], 13)) as L.Control.EasyButton
  const helpButton = createHelpModal(map)
  const locateButton = L.easyButton('fa-solid fa-location-crosshairs', (_, map) => { map.locate({ setView: true, maxZoom: 16 }) }) as L.Control.EasyButton

  L.easyBar([homeButton, locateButton, helpButton], { position: 'topleft' }).addTo(map)

  new L.Hash(map)
  // @ts-expect-error — leaflet.fullscreen v5 type not yet updated
  new L.Control.FullScreen({ position: 'topleft', forceSeparateButton: false }).addTo(map)

  BASEMAPS['CartoDB Positron'].addTo(map)
  L.control.layers(
    BASEMAPS,
    {
      ['River']: riverLayer,
      ['Trail']: trailLayer,
      [`Locations (${locationsGeoJSON.features.length.toLocaleString()})`]: locationsLayer,
      [`Spills (${(spills ?? 0).toLocaleString()})`]: spillsLayer,
      [`Tank Batteries (${tanksGeoJSON.features.length.toLocaleString()})`]: tanksLayer,
      [`Wells (${(wells ?? 0).toLocaleString()})`]: wellsLayer,
    },
    { position: 'topleft', collapsed: true }
  ).addTo(map)

  return map
}